import { Component, Ref } from 'vue-property-decorator'
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import MorphologicCodeModal from '@/modules/configurationModule/detail/samples/morphology/MorphologicCodeModal.vue'
import CodificationSearcherModal from '@/modules/configurationModule/detail/samples/CodificationSearcherModal.vue'
import { IContentMorphologicCodeGrid, initialMorphologicCodeGridState } from '@/modules/configurationModule/detail/samples/morphology/MorphologicCode.models'
import { morphologicCodeConfigurationServices } from '@/api/resources/configuration/samples/morphologicCode/morphologicCodeConfigurationServices'
import { GetContextMenuItemsParams, MenuItemDef, RowNode } from '@ag-grid-enterprise/all-modules'
import { BaseCrud } from '@/shared/models/cruds.models'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import { EntityIdentifiersRequest } from '@/shared/models/http/http.model'
import { StrictNullable } from '@/shared/types/app.types'

@Component({
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        MorphologicCodeModal,
        CodificationSearcherModal
    }
})
export default class MorphologicCodeGrid extends BaseCrudServerSideComponent<IContentMorphologicCodeGrid> implements BaseCrud {
    @Ref() readonly morphologicCodeModalWrapper!: any
    @Ref() readonly codificationSearcherModalWrapper!: any

    constructor() {
        super()
        this.state = initialMorphologicCodeGridState()
    }

    showCodificationSearcherModal(): void {
        this.codificationSearcherModalWrapper.show()
    }

    getContextMenuItems(params:GetContextMenuItemsParams): MenuItemDef[] {
        return [
            {
                name: this.$tc('configuration.actions.update'),
                cssClasses: this.state.selectedRows.length > 1 ? ['not-show'] : [''],
                action: () => {
                    const nodeSelected:StrictNullable<RowNode> = params?.node
                    const selectedRow:IContentMorphologicCodeGrid = nodeSelected?.data
                    return this.showModal(this.$tc('configuration.actions.update'), selectedRow, this.morphologicCodeModalWrapper)
                }
            },
            {
                name: this.$tc('configuration.actions.delete'),
                cssClasses: this.state.selectedRows.length < 1 ? ['not-show'] : [''],
                action: () => this.deleteOperation(this.state.selectedRows)
            },
            {
                name: params?.node?.data.active ? this.$tc('configuration.actions.deactivate') : this.$tc('configuration.actions.activate'),
                cssClasses: this.state.selectedRows.length ? ['not-show'] : [''],
                action: () => this.activateDeactivateSelection(!params?.node?.data?.active, [params?.node?.data])
            },
            {
                name: this.$tc('configuration.actions.activate'),
                cssClasses: this.canActivateBatch ? [''] : ['not-show'],
                action: () => this.activateDeactivateSelection(true, this.state.selectedRows)
            },
            {
                name: this.$tc('configuration.actions.deactivate'),
                cssClasses: this.canDeactivateBatch ? [''] : ['not-show'],
                action: () => this.activateDeactivateSelection(false, this.state.selectedRows)
            }
        ]
    }

    async createOperation(data: IContentMorphologicCodeGrid): Promise<void> {
        const response = await this.$_toCallUnique(morphologicCodeConfigurationServices.create(data))
        if (response) {
            this.morphologicCodeModalWrapper.hide()
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.created'))
        }
        this.reloadData()
    }

    async patchOperation(data: IContentMorphologicCodeGrid[]): Promise<void> {
        const response = await this.$_toCallUnique(morphologicCodeConfigurationServices.patchInBatch(data))
        if (response) {
            this.morphologicCodeModalWrapper.hide()
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 1))
        }
        this.reloadData()
    }

    async activateDeactivateSelection(option: boolean, selectionBatch: IContentMorphologicCodeGrid[]): Promise<void> {
        const morphologicCodeToPatch = selectionBatch?.filter((morphology: IContentMorphologicCodeGrid) => morphology.active !== option)?.map((morphology:IContentMorphologicCodeGrid) => {
            return {
                ...morphology,
                active: option
            }
        })
        if (morphologicCodeToPatch.length) {
            const errors = await this.$_toCallUnique(morphologicCodeConfigurationServices.patchInBatch(morphologicCodeToPatch))
            if (!errors) {
                if (selectionBatch.length > 1) {
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 2, { elements: selectionBatch.length }))
                } else {
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 1))
                }
            }
            this.reloadData()
        }
    }

    async deleteBatchOperation(topographies: IContentMorphologicCodeGrid[]): Promise<void> {
        const morphologicCodeToDelete: EntityIdentifiersRequest = {
            entityIdentifiers: topographies.map((morphology: IContentMorphologicCodeGrid) => Number(morphology?.id))
        }
        const errors = await this.$_toCallUnique(morphologicCodeConfigurationServices.deleteInBatch(morphologicCodeToDelete))
        if (!errors) {
            if (topographies.length > 1) {
                this.$_showToastSuccess(
                    this.$tc('configuration.toastMessages.deleted', 2, { elements: topographies?.length })
                )
            } else {
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted', 1))
            }
        }
        this.reloadData()
    }

    async deleteOperation(topographies: IContentMorphologicCodeGrid[]): Promise<void> {
        if (topographies.length) {
            const selectedRowsSize:number = this.state?.selectedRows?.length
            const labelConfirmation:string = this.$tc('configuration.grid.deleteConfirmation')
            const labelGrid: string = this.$tc('configuration.grid.element', selectedRowsSize)
            const labelDeleteQuestion:string = this.$tc('configuration.grid.deleteQuestion')
            const labelDeleteAction:string = this.$tc('configuration.actions.delete')
            const promise = await this.$_showConfirmationModal(
                `${labelConfirmation} ${selectedRowsSize} ${labelGrid}, <br/> ${labelDeleteQuestion}`,
                labelDeleteAction
            )
            if (promise) {
                await this.deleteBatchOperation(topographies)
            }
        }
    }
}
